import { Form, TextArea, TextField, useForm } from '@/components/form';
import { createModal } from '@/hooks/system/create-modal';
import { api } from '@/services/api/services-api';
import { sentryCaptureException } from '@/utils/sentry';
import { Button, Modal, ModalBody, ModalFooter, useSnackbars, Stack, Text } from '@customerio/pluma-components/react';
import { z } from 'zod';
import { useAuth } from '@/services/auth';
import { useAnalytics } from '@/services/analytics';
import { useEffect, useRef } from 'react';

export const useFeedbackModal = createModal('feedback', {
  type: 'ephemeral',
});

interface FeedbackModalProps {
  feature: string;
  eventCategory?: string;
}

export function FeedbackModal({ feature, eventCategory = 'feedback' }: FeedbackModalProps) {
  const auth = useAuth();
  const feedbackModal = useFeedbackModal();
  const snackbars = useSnackbars();
  const analytics = useAnalytics();

  const form = useForm({
    defaultValues: {
      feedback: '',
      replyTo: auth.user?.email ?? '',
    },
    validators: {
      onChange: z.object({
        feedback: z.string().min(1, 'Feedback is required'),
        replyTo: z.string().email('Please enter a valid email address').min(1, 'Reply to is required'),
      }),
    },
    async onSubmit(values) {
      try {
        await api.submitFeedback({
          params: { accountId: auth.account.id },
          body: {
            replyTo: values.value.replyTo,
            accountId: auth.account.id,
            userFirstName: auth.user.first_name,
            userLastName: auth.user.last_name,
            browser: navigator.userAgent,
            url: window.location.href,
            windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
            criticism: `${feature} Feedback`,
            praise: values.value.feedback,
          },
        });

        // Track feedback submission if eventCategory is a valid analytics namespace
        // Note: Only 'aiAssistant' namespace has 'feedbackFormSubmitted' event
        if (eventCategory === 'aiAssistant') {
          analytics.track('aiAssistant', 'feedbackFormSubmitted', {
            Feedback: values.value.feedback,
            Feature: feature,
          });
        }

        snackbars.create({
          message: 'Thank you for your feedback!',
          variant: 'success',
        });

        handleClose();
      } catch (error) {
        sentryCaptureException(error);
        snackbars.create({
          message: 'Unable to submit feedback. Please try again.',
          variant: 'error',
        });
      }
    },
  });

  // Track previous open state to only reset when modal opens (not on every render)
  // We intentionally exclude `form` from deps - its reference changes on every render
  // but we only want to reset when transitioning from closed to open
  const wasOpenRef = useRef(feedbackModal.isOpen);
  useEffect(() => {
    // Only reset when transitioning from closed to open
    if (feedbackModal.isOpen === true && wasOpenRef.current === false) {
      form.reset();
    }
    wasOpenRef.current = feedbackModal.isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form ref changes every render, only trigger on open state change
  }, [feedbackModal.isOpen]);

  const handleClose = () => {
    feedbackModal.close();
    form.reset();
  };

  const isSubmitDisabled = form.state.isValid !== true || form.state.isSubmitting;

  return (
    <Modal
      isOpen={feedbackModal.isOpen}
      onClose={handleClose}
      title={`${feature} Feedback`}
      size="md"
      shouldCloseOnOverlayClick={false}
    >
      <Form api={form}>
        <ModalBody>
          <Text mb="200" color="subtle">
            Let us know how we can improve {feature.toLowerCase()}! Your feedback will go directly to the team working
            on this feature.
          </Text>

          <Stack gap="100" mt="200">
            <TextArea
              api={form}
              name="feedback"
              label="Share your feedback"
              placeholder="Share your feedback..."
              minRows={3}
              maxRows={8}
              id="feedback-textarea"
            />

            <TextField
              api={form}
              name="replyTo"
              label="Reply to"
              placeholder={auth.user?.email ?? 'your@email.com'}
              type="email"
              id="reply-to-input"
            />
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isDisabled={isSubmitDisabled}>
            Submit feedback
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
